"use client"

import { RadialBar, RadialBarChart, Label } from "recharts"
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "../../../shared/components/ui/chart"
import Link from "next/link"
import { useEffect, useState } from "react"
import axios from "axios"

interface WorkOrderStatus {
  totalWorkOrders: number
  completed: number
  inProgress: number
  pending: number
  percentageComplete: string
}

export function WorkOrderStatusRadialChart() {
  const [workStatusData, setWorkStatusData] = useState<WorkOrderStatus | null>(null)

  const getWorkStatusByRegion = async (region: string) => {
    try {
      const response = await axios.get(
        `/api/reports/work-order-status`,
        {
          params: { region },
        }
      )
      return response.data.data.data as WorkOrderStatus
    } catch (error: any) {
      console.error("Error fetching work order status:", error.response?.data || error.message)
      throw error
    }
  }

  useEffect(() => {
    getWorkStatusByRegion("")
      .then((data) => {
        setWorkStatusData(data)
      })
      .catch((err) => console.error("Error fetching work status:", err))
  }, [])

  // Build chartData dynamically
  const chartData = workStatusData
    ? [
        { key: "inProgress", value: workStatusData.inProgress, fill: "#FFA500", name: "In Progress" },
        { key: "completed", value: workStatusData.completed, fill: "#3E7C59", name: "Completed" },
        { key: "pending", value: workStatusData.pending, fill: "#003764", name: "Pending" },
      ]
    : []

  // Dynamic chartConfig
  const chartConfig: ChartConfig = {
    inProgress: { label: "In Progress" },
    completed: { label: "Completed" },
    pending: { label: "Pending" },
  }

  return (
    <div>
      <ChartContainer
        config={chartConfig}
        className="mx-auto aspect-square max-h-[250px]"
      >
        <RadialBarChart data={chartData} innerRadius={70} outerRadius={110}>
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent hideLabel nameKey="key" />}
          />
          <RadialBar dataKey="value" background />
          <ChartLegend
            verticalAlign="bottom"
            content={<ChartLegendContent nameKey="key" />}
            className="translate-y-2 w-full gap-2 [&>*]:w-full [&>*]:justify-center"
          />

          {/*  Optional: Show % complete in center */}
          {workStatusData && (
            <Label
              content={({ viewBox }) => {
                if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="fill-foreground text-lg font-bold"
                    >
                      {workStatusData.percentageComplete}%
                    </text>
                  )
                }
              }}
            />
          )}
        </RadialBarChart>
      </ChartContainer>

      <Link
        className="text-center text-primary mx-auto mt-5 mb-5 block underline"
        href={"#"}
      >
        View Active Work Orders
      </Link>
    </div>
  )
}
